import React, { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate, useParams } from "react-router-dom";

import api from '../../utils/webcall';
import { getErrorMessage } from '../../utils/utils';
import UserProfile from '../../components/User';
import AccessController from '../../components/access_controller';

const currency = (value, code) =>
    `${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })} ${code || 'INR'}`;

/** Per-unit price, guarding the divide-by-zero a blank quantity would cause. */
function pricePerUnit(item) {
    const price = parseFloat(item['total_price']);
    const quantity = parseFloat(item['quantity']);
    if (!Number.isFinite(price) || !Number.isFinite(quantity) || quantity === 0) return null;
    return price / quantity;
}

/**
 * One bill photo.
 *
 * Bill images have been broken for a long time - the upload wrote files to one
 * place and stored a URL pointing at another - so a load failure is a state
 * this component has to handle properly rather than leaving the browser's
 * broken-image glyph. It says what went wrong and offers the URL, which is what
 * someone debugging it actually needs.
 */
function BillImage(props) {
    const { url, index, onOpen } = props;
    const [status, setStatus] = useState('loading');

    if (status === 'error') {
        return (
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <BrokenImageIcon fontSize="small" color="warning" />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Image {index + 1} could not be loaded
                    </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', wordBreak: 'break-all' }}>
                    {url}
                </Typography>
                <Box sx={{ mt: 1 }}>
                    <Button size="small" endIcon={<OpenInNewIcon />}
                        href={url} target="_blank" rel="noreferrer">
                        Try opening directly
                    </Button>
                </Box>
            </Paper>
        );
    }

    return (
        <Paper
            variant="outlined"
            onClick={() => onOpen(url)}
            sx={{
                p: 0, overflow: 'hidden', cursor: 'zoom-in',
                position: 'relative', height: 220,
            }}
        >
            {/* The skeleton is overlaid rather than swapped in. An <img> that is
                display:none with loading="lazy" is never fetched at all, so
                neither onLoad nor onError ever fires and the placeholder stays
                forever - which is exactly how this first behaved. Keeping the
                image in layout and fading it in avoids that. */}
            {status === 'loading' && (
                <Skeleton
                    variant="rectangular"
                    sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                />
            )}
            <Box
                component="img"
                src={url}
                alt={`Bill photo ${index + 1}`}
                loading="lazy"
                onLoad={() => setStatus('ok')}
                onError={() => setStatus('error')}
                sx={{
                    width: '100%', height: '100%', objectFit: 'cover',
                    opacity: status === 'ok' ? 1 : 0,
                    transition: 'opacity 200ms',
                }}
            />
        </Paper>
    );
}

/** Full-screen viewer - a bill photo is unreadable at thumbnail size. */
function ImageLightbox(props) {
    const { url, onClose } = props;
    return (
        <Modal open={Boolean(url)} onClose={onClose}>
            <Box
                onClick={onClose}
                sx={{
                    position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.92)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    p: 1, cursor: 'zoom-out',
                }}
            >
                <IconButton
                    onClick={onClose}
                    aria-label="Close image"
                    sx={{ position: 'absolute', top: 8, right: 8, color: 'common.white' }}
                >
                    <CloseIcon />
                </IconButton>
                <Box
                    component="img"
                    src={url}
                    alt="Bill photo, full size"
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                        maxWidth: '100%', maxHeight: '100%',
                        objectFit: 'contain', cursor: 'default',
                    }}
                />
            </Box>
        </Modal>
    );
}

function ViewBillDetails(props) {
    const { showErrorMessage, showSuccessMessage } = props;
    const isMobile = useMediaQuery('(max-width:600px)');
    const navigate = useNavigate();
    // Route is /bill/view/* so the id is the wildcard remainder. Previously this
    // was pulled off window.location.pathname by hand, which broke on a trailing
    // slash or query string.
    const params = useParams();
    const billId = (params['*'] || '').split('/').filter(Boolean).pop() || '';

    const [billData, setBillData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [lightboxUrl, setLightboxUrl] = useState(null);

    const loadBillDetails = useCallback(() => {
        setLoading(true);
        api.get(`/pos/bill/details/${billId}`).then((res) => {
            const data = res.data;
            let parsed = data['bills']['json_data'];
            if (typeof parsed === 'string') {
                try { parsed = JSON.parse(parsed); } catch (e) { parsed = {}; }
            }
            data['bills']['json_data'] = parsed || {};
            setBillData(data['bills']);
        }).catch((error) => {
            showErrorMessage(getErrorMessage(error));
        }).finally(() => {
            setLoading(false);
            setBusy(false);
        });
    }, [billId, showErrorMessage]);

    useEffect(() => { loadBillDetails(); }, [loadBillDetails]);

    const approveBill = () => {
        setBusy(true);
        api.post(`/pos/bill/approve/${billId}/`).then((res) => {
            if (res.status === 200) showSuccessMessage('Bill approved');
        }).catch((error) => {
            showErrorMessage(getErrorMessage(error));
        }).finally(loadBillDetails);
    };

    const deleteBill = () => {
        setDeleting(true);
        api.post(`/pos/bill/delete/${billId}/`).then((res) => {
            if (res.status === 200) {
                showSuccessMessage('Bill deleted');
                navigate('/bill/view');
            }
        }).catch((error) => {
            showErrorMessage(getErrorMessage(error));
        }).finally(() => {
            setDeleting(false);
            setConfirmOpen(false);
        });
    };

    const json = billData?.json_data || {};
    const items = json['data'] || [];
    const images = json['saved_bills_path'] || [];
    const approved = Boolean(billData?.['approved']);
    const submitter = billData?.['user']
        ? `${billData['user']['first_name'] || ''} ${billData['user']['last_name'] || ''}`.trim()
        : '';

    function renderSummary() {
        const facts = [
            { label: 'Date', value: billData['submitted_date'] },
            { label: 'Total', value: currency(billData['total_amount'], billData['currency']) },
            { label: 'Items', value: billData['number_of_items'] },
            { label: 'Photos', value: images.length },
            { label: 'Submitted by', value: submitter || 'Unknown' },
        ];
        return (
            <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Grid container spacing={2}>
                    {facts.map((fact) => (
                        <Grid size={{ xs: 6, sm: 4, md: 2.4 }} key={fact.label}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {fact.label}
                            </Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                                {fact.value}
                            </Typography>
                        </Grid>
                    ))}
                </Grid>
            </Paper>
        );
    }

    function renderItems() {
        if (items.length === 0) {
            return (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No line items recorded on this bill.
                </Typography>
            );
        }

        // On a phone each item becomes a card: a four-column table either scrolls
        // sideways or truncates, and neither is readable while holding a receipt.
        if (isMobile) {
            return (
                <Box>
                    {items.map((item, index) => {
                        const unit = pricePerUnit(item);
                        return (
                            <Paper variant="outlined" key={`item-${index}`}
                                sx={{ p: 1.5, mb: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between',
                                    gap: 1, alignItems: 'baseline' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {item['item_name']}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                        {currency(item['total_price'], billData['currency'])}
                                    </Typography>
                                </Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {item['quantity']} {item['measurement_unit']}
                                    {unit !== null && ` · ${unit.toFixed(2)} per ${item['measurement_unit']}`}
                                </Typography>
                            </Paper>
                        );
                    })}
                </Box>
            );
        }

        return (
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Item</TableCell>
                            <TableCell align="right">Quantity</TableCell>
                            <TableCell align="right">Total price</TableCell>
                            <TableCell align="right">Price per unit</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((item, index) => {
                            const unit = pricePerUnit(item);
                            return (
                                <TableRow key={`item-${index}`}>
                                    <TableCell>{item['item_name']}</TableCell>
                                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                        {item['quantity']} {item['measurement_unit']}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                        {currency(item['total_price'], billData['currency'])}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                        {unit === null ? '--' : `${unit.toFixed(2)} / ${item['measurement_unit']}`}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    function renderImages() {
        if (images.length === 0) {
            return (
                <Alert severity="info">
                    No photos were attached to this bill.
                </Alert>
            );
        }
        return (
            <Grid container spacing={1.5}>
                {images.map((url, index) => (
                    <Grid size={{ xs: 6, sm: 4, md: 3 }} key={`${url}-${index}`}>
                        <BillImage url={url} index={index} onOpen={setLightboxUrl} />
                    </Grid>
                ))}
            </Grid>
        );
    }

    return (
        <AccessController onlyAdmin={false}>
            <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />

            <Modal open={confirmOpen} onClose={() => !deleting && setConfirmOpen(false)}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: isMobile ? '90%' : 420, maxWidth: 420,
                    bgcolor: 'background.paper', borderRadius: 1, boxShadow: 24,
                    p: isMobile ? 2 : 3,
                }}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <AlertTitle>Delete bill #{billId}?</AlertTitle>
                        This cannot be undone. The attached photos stay on the server
                        but the bill and its line items are removed.
                    </Alert>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button fullWidth variant="outlined" disabled={deleting}
                            onClick={() => setConfirmOpen(false)}>
                            Keep bill
                        </Button>
                        <Button fullWidth variant="contained" color="error"
                            disabled={deleting} onClick={deleteBill}>
                            {deleting ? 'Deleting...' : 'Delete'}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
                {/* Header: identity and state first, then the actions that apply
                    to that state. */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <IconButton aria-label="Back to bills" onClick={() => navigate('/bill/view')}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Bill #{billId}
                    </Typography>
                    {!loading && billData && (
                        <Chip
                            size="small"
                            color={approved ? 'success' : 'default'}
                            variant={approved ? 'filled' : 'outlined'}
                            label={approved ? 'Approved' : 'Not approved'}
                        />
                    )}
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : !billData ? (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        This bill could not be loaded.
                    </Alert>
                ) : (
                    <>
                        {/* Only offer actions the bill's state allows: approving an
                            approved bill, or deleting one, are not available. */}
                        {(!approved) && (
                            <Box sx={{ display: 'flex', gap: 1, my: 2, flexWrap: 'wrap' }}>
                                {UserProfile.isManager() && (
                                    <Button variant="contained" onClick={approveBill}
                                        disabled={busy} fullWidth={isMobile}
                                        sx={{ flex: isMobile ? '1 1 100%' : '0 0 auto' }}>
                                        {busy ? 'Working...' : 'Approve bill'}
                                    </Button>
                                )}
                                <Button variant="outlined" color="error"
                                    onClick={() => setConfirmOpen(true)} disabled={busy}
                                    fullWidth={isMobile}
                                    sx={{ flex: isMobile ? '1 1 100%' : '0 0 auto' }}>
                                    Delete bill
                                </Button>
                            </Box>
                        )}

                        <Box sx={{ mt: 2 }}>{renderSummary()}</Box>

                        <Divider sx={{ my: 3 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            Bill photos
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                            Tap a photo to view it full size.
                        </Typography>
                        {renderImages()}

                        <Divider sx={{ my: 3 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
                            Line items
                        </Typography>
                        {renderItems()}
                    </>
                )}
            </Box>
        </AccessController>
    );
}

export default ViewBillDetails;
